import React, { Fragment } from 'react';
import { Box, Divider, List, Typography, Fade, Skeleton, Button } from '@mui/material';
import { SearchOffOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SingleSearchResult from './SingleSearchResult';

export default function SearchResult(props) {
  const {
    showProductList,
    resultBoxRef,
    searchResultData,
    resultNotFound,
    loading,
    searchTerm,
    selectedIndex,
  } = props;

  const navigate = useNavigate();

  return (
    <Fade in={showProductList} timeout={250}>
      <Box
        ref={resultBoxRef}
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          mt: 1,
          display: showProductList ? "block" : "none",
          backgroundColor: "#ffffff",
          maxHeight: {
            xs: "380px",
            sm: "420px",
            md: "460px",
          },
          width: "100%",
          zIndex: 9999,
          borderRadius: "14px",
          boxShadow: "0 16px 40px rgba(0, 34, 85, 0.12), 0 2px 8px rgba(0,0,0,0.04)",
          overflow: "hidden",
          border: "1px solid #E4C18F",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Results Header Count */}
        {!loading && searchResultData?.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 0.8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#FAF7F2",
              borderBottom: "1px solid rgba(228, 193, 143, 0.35)",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#8D5A24",
                fontFamily: "Be Vietnam Pro",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Matching Cards ({searchResultData.length})
            </Typography>
          </Box>
        )}

        {/* Results List / Loading / Empty */}
        <Box
          sx={{
            overflowY: "auto",
            flex: 1,
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f5f5f5",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#E4C18F",
              borderRadius: "4px",
            },
          }}
        >
          <List disablePadding>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3].map((key) => (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1,
                    }}
                  >
                    <Skeleton
                      variant="rounded"
                      width={50}
                      height={36}
                      sx={{ borderRadius: "8px", flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="65%" height={20} />
                      <Skeleton variant="text" width="35%" height={14} />
                    </Box>
                    <Skeleton
                      variant="rounded"
                      width={70}
                      height={24}
                      sx={{ borderRadius: "6px" }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                {searchResultData?.map((item, index) => (
                  <Fragment key={item?.id || index}>
                    <SingleSearchResult
                      item={item}
                      searchTerm={searchTerm}
                      isSelected={index === selectedIndex}
                    />
                    {index < searchResultData.length - 1 && (
                      <Divider sx={{ mx: 2, borderColor: "rgba(0,0,0,0.05)" }} />
                    )}
                  </Fragment>
                ))}
              </>
            )}

            {resultNotFound && !loading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                  px: 3,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: "rgba(228, 193, 143, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <SearchOffOutlined sx={{ fontSize: 30, color: "#8D5A24" }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#1a1a1a",
                    fontFamily: "Be Vietnam Pro",
                    mb: 0.5,
                  }}
                >
                  No Gift Cards Found
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#6B7280",
                    fontFamily: "Be Vietnam Pro",
                    maxWidth: 280,
                    mb: 2,
                  }}
                >
                  We couldn't find matches for "{searchTerm}". Check the spelling or browse all cards.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    navigate('/card-list');
                  }}
                  sx={{
                    textTransform: "none",
                    borderColor: "#E4C18F",
                    color: "primary.main",
                    fontSize: "12px",
                    fontWeight: 600,
                    fontFamily: "Be Vietnam Pro",
                    borderRadius: "8px",
                    px: 2,
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "rgba(228, 193, 143, 0.1)",
                    },
                  }}
                >
                  Explore All Gift Cards
                </Button>
              </Box>
            )}
          </List>
        </Box>

        {/* Quick Action Footer */}
        {!loading && !resultNotFound && searchResultData?.length > 0 && (
          <Box
            onClick={() => navigate('/card-list')}
            sx={{
              px: 2,
              py: 1,
              borderTop: "1px solid rgba(228, 193, 143, 0.3)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "#FAF7F2",
              cursor: "pointer",
              transition: "background 0.2s",
              "&:hover": {
                bgcolor: "#F3EBDD",
              },
            }}
          >
            <Typography
              sx={{
                color: "primary.main",
                fontWeight: 600,
                fontSize: "12px",
                fontFamily: "Be Vietnam Pro",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              Explore all gift cards →
            </Typography>
          </Box>
        )}
      </Box>
    </Fade>
  );
}